import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { API_BASE_URL } from "@/lib/apiBase";
import { withReturnQuery } from "@/lib/permalinks";

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Org settings → Files: org quota, per-team usage; open a team to browse files.
 * Back from the team page returns here via return=.
 */
const OrgFilesSettingsPanel = ({ orgId, accessToken }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState({
    used_bytes: 0,
    quota_bytes: null,
    teams: [],
    breakdown: null,
  });

  const load = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/org/${orgId}/files/usage`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const q = res.data?.quota_bytes;
      setUsage({
        used_bytes: Number(res.data?.used_bytes) || 0,
        quota_bytes: q == null || q === "" ? null : Number(q),
        teams: Array.isArray(res.data?.teams) ? res.data.teams : [],
        breakdown: res.data?.breakdown || null,
      });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to load file storage usage"
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const usagePct = usage.quota_bytes
    ? Math.min(100, (100 * usage.used_bytes) / usage.quota_bytes)
    : 0;

  const openTeamFiles = (team) => {
    const returnTo = `/orgs/${encodeURIComponent(orgId)}/settings?section=files`;
    const href = `/teams/${team.team_id}?org_id=${encodeURIComponent(
      orgId
    )}&tab=files`;
    navigate(withReturnQuery(href, returnTo));
  };

  const quotaLabel =
    usage.quota_bytes != null
      ? formatBytes(usage.quota_bytes)
      : "your plan’s storage limit";

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-base font-semibold">File storage</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Shared {quotaLabel} across all teams (files and post attachments). Open
          a team to browse or manage its folders and files.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Organization total</span>
              <span className="font-medium tabular-nums">
                {formatBytes(usage.used_bytes)} /{" "}
                {usage.quota_bytes == null
                  ? "Unlimited"
                  : formatBytes(usage.quota_bytes)}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${usagePct}%` }}
              />
            </div>
          </div>

          {usage.breakdown ? (
            <ul className="ml-1 space-y-1 border-l border-border pl-4 text-xs text-muted-foreground">
              <li className="flex justify-between gap-3">
                <span>Team files</span>
                <span className="tabular-nums">
                  {formatBytes(usage.breakdown.team_files_bytes)}
                </span>
              </li>
              <li className="flex justify-between gap-3">
                <span>Post attachments</span>
                <span className="tabular-nums">
                  {formatBytes(usage.breakdown.post_attachments_bytes)}
                </span>
              </li>
              <li className="flex justify-between gap-3">
                <span>Comment attachments</span>
                <span className="tabular-nums">
                  {formatBytes(usage.breakdown.comment_attachments_bytes)}
                </span>
              </li>
              <li className="flex justify-between gap-3">
                <span>Message media</span>
                <span className="tabular-nums">
                  {formatBytes(usage.breakdown.dm_media_bytes)}
                </span>
              </li>
            </ul>
          ) : null}

          {usage.teams.length === 0 ? (
            <p className="text-sm text-muted-foreground">No teams yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {usage.teams.map((team) => {
                const teamPct = usage.quota_bytes
                  ? Math.min(
                      100,
                      (100 * team.used_bytes) / usage.quota_bytes
                    )
                  : 0;
                return (
                  <li key={team.team_id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-muted/40"
                      onClick={() => openTeamFiles(team)}
                    >
                      <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="text-sm font-medium truncate">
                            {team.name}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                            {formatBytes(team.used_bytes)}
                            {team.file_count > 0
                              ? ` · ${team.file_count} file${
                                  team.file_count === 1 ? "" : "s"
                                }`
                              : ""}
                          </span>
                        </div>
                        <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-primary/70 transition-all"
                            style={{ width: `${teamPct}%` }}
                          />
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default OrgFilesSettingsPanel;
